import { ApiError } from '@/api/errors';
import type { EventData } from '@/lib/analytics/umami-client';
import { fromApi } from '@/utils/datetime';
import type { OnlineOrder } from '@restaurant/api-client';

/**
 * One builder for every online-order analytics event, so the schema has a single
 * source of truth and no call site can invent its own dimension names.
 *
 * Deliberately absent from the output: order ids, confirmation codes, customer
 * name/phone/address, item names, note contents and cancellation reasons. What
 * ships is the shape of the decision, never who it was about - `order-analytics.spec.ts`
 * asserts that as a regression guard.
 */
interface OrderFacts {
  /** The row the operator was looking at. Mutation results null out
   *  `paymentMethodKind`, so the dimension has to come from the query row. */
  order: OnlineOrder;
}

/** Which pane an action was fired from. A discriminated union rather than one
 *  loose bag: `opened` carries `surface` and every other action carries `from`,
 *  and forgetting either should be a type error, not a silently missing column. */
export type OrderActionFacts =
  | (OrderFacts & { action: 'opened'; surface: 'docked' | 'page'; via?: 'push' })
  | (OrderFacts & { action: 'printed'; from: 'inspector'; tickets?: number })
  | (OrderFacts & {
      action: 'accept';
      from: 'list' | 'inspector';
      prepMinutes: number;
      prepDelta: number;
      prepFallback: boolean;
      travelMinutes?: number;
      travelDelta?: number;
      dwellSeconds: number;
    })
  | (OrderFacts & { action: 'accept_dismissed'; from: 'list' | 'inspector'; dwellSeconds: number })
  | (OrderFacts & { action: 'reject'; from: 'list' | 'inspector' })
  | (OrderFacts & {
      action: 'done';
      from: 'list' | 'inspector';
      sinceLastDoneSeconds?: number;
    });

export interface OrderActionOutcome {
  outcome: 'ok' | 'already_taken' | 'error';
  errorCode?: string;
}

/** Whole seconds between two epoch millis. Clamped: a server clock running ahead
 *  must read as "just now", not as a negative age. */
export function elapsedSeconds(fromMs: number, nowMs: number): number {
  return Math.max(0, Math.round((nowMs - fromMs) / 1000));
}

/** Gap since the previous completion, for telling real-time clearing from an
 *  end-of-shift sweep. Undefined on the first one of the page's life. */
export function sinceLastDone(prevMs: number | null, nowMs: number): number | undefined {
  return prevMs === null ? undefined : elapsedSeconds(prevMs, nowMs);
}

/** Bounded enum from our own contract. The server's prose (`detail`) and the
 *  per-request `traceId` never become dimensions. */
export function errorCodeOf(err: unknown): string {
  return err instanceof ApiError ? err.code : 'network';
}

export function orderActionPayload(
  f: OrderActionFacts & OrderActionOutcome,
  nowMs: number,
): EventData {
  const { order } = f;
  const payload: EventData = {};
  // An absent dimension must be absent, not present-and-undefined: Umami writes
  // one event_data row per key, and a pickup order has no travel leg to report.
  const put = (key: string, value: string | number | boolean | undefined): void => {
    if (value !== undefined) payload[key] = value;
  };

  put('action', f.action);
  put('outcome', f.outcome);
  put('errorCode', f.errorCode);
  put('orderType', order.type.toLowerCase());
  put('ageSeconds', elapsedSeconds(fromApi(order.createdAt).toMillis(), nowMs));
  put(
    'visibleAgeSeconds',
    order.operatorVisibleAt
      ? elapsedSeconds(fromApi(order.operatorVisibleAt).toMillis(), nowMs)
      : undefined,
  );
  put('scheduled', order.scheduledFor !== null);
  put('totalCents', order.totalCents);
  put('itemCount', order.items.length);
  put('paymentKind', order.paymentMethodKind?.toLowerCase());

  if (f.action === 'opened') {
    put('surface', f.surface);
    put('via', f.via);
    put('status', order.status.toLowerCase());
    put('hasNote', !!order.customerNote);
    return payload;
  }

  put('from', f.from);
  if (f.action === 'accept') {
    put('prepMinutes', f.prepMinutes);
    put('prepDelta', f.prepDelta);
    put('prepFallback', f.prepFallback);
    put('travelMinutes', f.travelMinutes);
    put('travelDelta', f.travelDelta);
    put('dwellSeconds', f.dwellSeconds);
  }
  if (f.action === 'accept_dismissed') put('dwellSeconds', f.dwellSeconds);
  if (f.action === 'done') put('sinceLastDoneSeconds', f.sinceLastDoneSeconds);
  if (f.action === 'printed') put('tickets', f.tickets);

  return payload;
}
